import { graphHooks } from '@tunasong/graph-lib/react'
import { getIconByName, Page, type IconName, type SvgIconComponent } from '@tunasong/icons'
import { getElementMedia, usePlugins } from '@tunasong/plugin-lib'
import type { Entity, Favorite, Persisted } from '@tunasong/schemas'
import { getColorCSS } from '../color/color.js'
import { useUserConfig } from '../config/config-service.hook.js'
import { getEntityPath } from '../entity/entity-link-util.js'
import { useCurrentLayoutName } from '../navigation/layout-nav.hook.js'
import { useLocation } from 'react-router'

interface FavoriteWithIcon extends Omit<Favorite, 'iconName'> {
  Icon: SvgIconComponent
}

export const useFavorites = () => {
  const [userFavorites, setFavorites] = useUserConfig('favorites')

  // @legacy favorites that are entityIds
  const favoriteIds = userFavorites?.filter(f => typeof f === 'string') ?? []
  const restFavorites = userFavorites?.filter(f => typeof f !== 'string') ?? []
  const { entities = [] } = graphHooks.useEntitiesById(favoriteIds)
  const plugins = usePlugins('all')
  const layoutName = useCurrentLayoutName()

  const getFavoriteTemplateFromEntity = (entity: Persisted<Entity>): Favorite => {
    const layout = layoutName && layoutName !== 'Main' ? layoutName : ''
    const name = `${entity.name ?? entity.type}${layout ? ' (' + layout + ')' : ''}`
    return {
      path: getEntityPath({ entity, layout: layoutName }),
      name,
      entityType: entity.type,
    }
  }

  const legacyEntityFavorites: Favorite[] = entities.map(entity => getFavoriteTemplateFromEntity(entity))
  const favorites = [...restFavorites, ...legacyEntityFavorites]

  const addFavoriteEntity = (favoriteEntity: Persisted<Entity>) => {
    // Get the current layout of the entity
    const favorite = getFavoriteTemplateFromEntity(favoriteEntity)
    setFavorites([...(favorites ?? []), favorite])
  }

  // Get the current path
  const location = useLocation()

  const path = `${location.pathname}${location.search}`
  const currentFavorite = favorites.find(f => f.path === path)

  const addFavorite = (favorite: Omit<Favorite, 'path'>) => {
    setFavorites([
      ...(favorites ?? []),
      {
        ...favorite,
        path,
      },
    ])
  }

  const removeFavoriteEntity = (removeEntity: Persisted<Entity>) => {
    const spec = getFavoriteTemplateFromEntity(removeEntity)
    setFavorites((favorites ?? []).filter(favorite => favorite.path !== spec.path))
  }

  const removeFavorite = (favorite: Favorite) => {
    setFavorites((favorites ?? []).filter(f => f.path !== favorite.path))
  }

  const isFavorite = (entity?: Persisted<Entity>) => {
    if (!entity || favorites.length === 0) {
      return false
    }
    const generatedFavorite = getFavoriteTemplateFromEntity(entity)
    return favorites.some(f => typeof f !== 'string' && f.path === generatedFavorite.path)
  }

  const toggleFavorite = (favorite: Favorite) => {
    const existingFavorite = favorites.find(f => f.path === favorite.path)
    if (existingFavorite) {
      // Remove from favorites
      removeFavorite(existingFavorite)
      return
    }
    // Add to favorites
    setFavorites([...(favorites ?? []), favorite])
  }

  // Annotate the favorites with the icon, color, and media
  const annotatedFavorites: FavoriteWithIcon[] = favorites.map(f => {
    const media = f.entityType ? getElementMedia(f.entityType ?? '', plugins) : undefined
    const Icon = media?.icon ? media.icon : f.iconName ? getIconByName(f.iconName as IconName) : Page
    const color = media?.materialColor ? getColorCSS(media.materialColor) : f.color
    return {
      name: f.name,
      path: f.path,
      color,
      Icon,
    }
  })

  return {
    favorites: annotatedFavorites,
    // The favorite entry for the current page (if any)
    currentFavorite,
    addFavorite,
    addFavoriteEntity,
    toggleFavorite,
    removeFavorite,
    removeFavoriteEntity,
    isFavorite,
    getFavoriteTemplateFromEntity,
  }
}
